import * as React from 'react';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { cn } from '@/lib/utils';
import {
  getSessionTypeOptions,
  isRecurrenceWeekDayActive,
  toggleRecurrenceWeekDay,
} from '@/components/sessions/inperson/session-form-utils';
import {
  SESSION_IN_PERSON_FORMAT_OPTIONS,
  SESSION_IN_PERSON_MODE_OPTIONS,
  SESSION_IN_PERSON_RECURRENCE_OPTIONS,
  SESSION_RECURRENCE_SCOPE_CREATE_OPTIONS,
  SESSION_RECURRENCE_SCOPE_EDIT_OPTIONS,
  SESSION_WEEKDAY_SHORT_OPTIONS,
} from '@/components/sessions/session-configs';
import type {
  PlanificationType,
  RecurrenceUpdateScope,
  SessionFormData,
  SessionFormFieldChange,
  SessionFormFieldValidation,
} from '@/types/inperson/session.types';

/** Props for {@link TutoringSessionGeneralSection}. */
interface Props {
  form: SessionFormData;
  onFieldChange: SessionFormFieldChange;
  onBlur: (field: string) => void;
  validation: SessionFormFieldValidation;
  planificationType?: PlanificationType;
  showRecurrenceScope?: boolean;
  recurrenceUpdateScope?: RecurrenceUpdateScope;
  onRecurrenceScopeChange?: (scope: RecurrenceUpdateScope) => void;
  recurrenceMode?: 'create' | 'edit';
}

/** Type, format, and title for a tutoring session. */
export function TutoringSessionGeneralSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  planificationType,
  showRecurrenceScope = false,
  recurrenceUpdateScope = 'occurrence',
  onRecurrenceScopeChange,
  recurrenceMode = 'edit',
}: Readonly<Props>) {
  const sessionTypeOptions = getSessionTypeOptions(planificationType);
  const isSessionTypeLocked = planificationType === 'individual';
  const showRecurrence = form.mode === 'recurring';

  function handleTypeChange(value: string): void {
    onFieldChange('type', value);
  }
  function handleTypeBlur(): void {
    onBlur('type');
  }
  function handleFormatChange(value: string): void {
    onFieldChange('sessionFormat', value);
  }
  function handleFormatBlur(): void {
    onBlur('sessionFormat');
  }
  function handleModeChange(value: string): void {
    onFieldChange('mode', value);
  }
  function handleModeBlur(): void {
    onBlur('mode');
  }
  function handleRecurrenceTypeChange(value: string): void {
    onFieldChange('recurrenceType', value);
  }
  function handleRecurrenceTypeBlur(): void {
    onBlur('recurrenceType');
  }
  function handleRecurrenceScopeChange(value: string): void {
    onRecurrenceScopeChange?.(value as RecurrenceUpdateScope);
  }

  function renderWeekdayButton(option: { day: number; label: string }): React.ReactNode {
    const isActive = isRecurrenceWeekDayActive(form.recurrenceRule, option.day);
    function handleWeekdayClick(): void {
      onFieldChange('recurrenceRule', toggleRecurrenceWeekDay(form.recurrenceRule, option.day));
    }
    return (
      <Button
        key={option.day}
        variant="custom"
        size="sm"
        className={cn(
          'px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
          isActive ? 'filter-badge-active' : 'filter-badge-inactive',
        )}
        style={{ '--filter-color': 'var(--color-2-nuance-1)' } as React.CSSProperties}
        onClick={handleWeekdayClick}
      >
        {option.label}
      </Button>
    );
  }

  function renderRecurrenceType(): React.ReactNode {
    if (!showRecurrence) return null;
    return (
      <Select
        label="Récurrence"
        placeholder="Sélectionner une récurrence"
        options={SESSION_IN_PERSON_RECURRENCE_OPTIONS}
        value={form.recurrenceType}
        onChange={handleRecurrenceTypeChange}
        onBlur={handleRecurrenceTypeBlur}
        state={validation.getFieldState('recurrenceType')}
        error={validation.getFieldError('recurrenceType')}
        required
        size="sm"
      />
    );
  }

  function renderRecurrenceScope(): React.ReactNode {
    if (!showRecurrenceScope) return null;
    const scopeCopy =
      recurrenceMode === 'create'
        ? "Cette séance fait partie d'une série récurrente. Choisissez si les changements (informations et répartition des groupes) s'appliquent uniquement à cette date ou aussi aux séances suivantes de la même série. Chaque séance conserve sa propre date."
        : "Choisissez si les changements s'appliquent uniquement à cette date ou aussi aux séances suivantes du même créneau récurrent.";
    const scopeOptions =
      recurrenceMode === 'create'
        ? SESSION_RECURRENCE_SCOPE_CREATE_OPTIONS
        : SESSION_RECURRENCE_SCOPE_EDIT_OPTIONS;
    return (
      <div
        className="mt-4 rounded-lg border p-4 space-y-3"
        style={{ borderColor: 'var(--color-1-nuance-3)' }}
      >
        <p className="text-sm font-medium" style={{ color: 'var(--color-1-nuance-1)' }}>
          Portée de la modification
        </p>
        <p className="text-xs text-muted-foreground">{scopeCopy}</p>
        <RadioGroup
          name="recurrenceUpdateScope"
          options={scopeOptions}
          value={recurrenceUpdateScope}
          onValueChange={handleRecurrenceScopeChange}
        />
      </div>
    );
  }

  function renderWeekdayFieldset(): React.ReactNode {
    if (!showRecurrence || form.recurrenceType !== 'several_days_per_week') return null;
    const recurrenceError = validation.getFieldError('recurrenceRule');
    return (
      <fieldset className="mt-4">
        <legend className="text-sm font-medium text-foreground mb-2 block">
          Jours de la semaine <span className="text-red-500">*</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {SESSION_WEEKDAY_SHORT_OPTIONS.map(renderWeekdayButton)}
        </div>
        {recurrenceError ? <p className="text-xs text-red-500 mt-1">{recurrenceError}</p> : null}
      </fieldset>
    );
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <BookOpen className="w-4 h-4" />
        Informations générales
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Type de séance"
          placeholder="Sélectionner un type"
          options={sessionTypeOptions}
          value={form.type}
          onChange={handleTypeChange}
          onBlur={handleTypeBlur}
          state={validation.getFieldState('type')}
          error={validation.getFieldError('type')}
          required
          size="sm"
          disabled={isSessionTypeLocked}
        />
        <Select
          label="Format"
          placeholder="Sélectionner un format"
          options={SESSION_IN_PERSON_FORMAT_OPTIONS}
          value={form.sessionFormat}
          onChange={handleFormatChange}
          onBlur={handleFormatBlur}
          state={validation.getFieldState('sessionFormat')}
          error={validation.getFieldError('sessionFormat')}
          required
          size="sm"
          disabled
        />
        <Select
          label="Mode"
          placeholder="Sélectionner un mode"
          options={SESSION_IN_PERSON_MODE_OPTIONS}
          value={form.mode}
          onChange={handleModeChange}
          onBlur={handleModeBlur}
          state={validation.getFieldState('mode')}
          error={validation.getFieldError('mode')}
          required
          size="sm"
        />
        {renderRecurrenceType()}
      </div>
      {renderWeekdayFieldset()}
      {renderRecurrenceScope()}
    </div>
  );
}
